/**
 * WCC360 v2 — career worlds (12–14 "Explorers" output model).
 *
 * A twelve-year-old must never be handed a list of job titles. A job title
 * at twelve is a costume, not a plan — and the corpus contains 5,877 of
 * them, most of which won't exist in the shape they have today by the time
 * this learner starts working. So the 12–14 band outputs eight WORLDS: the
 * kinds of work a person can spend a life in.
 *
 * The map below is verified to cover every family in
 * `public/data/careers_bundle_v1.json` with no orphan family and no name
 * the bundle doesn't have. `worlds.test.ts` is what stops it rotting.
 *
 * Three families deliberately sit in two worlds each (Semiconductors &
 * Electronics, Marketing & Growth, Hospitality & Tourism). Do not
 * de-duplicate them: they genuinely read both ways to a child.
 */
import {
  WEIGHTS, effectiveCapacity, entryCost,
  scoreArchetype, scoreContext, scoreDomain, scoreDemand, scoreSalary, scoreValues,
  type Career, type Learner,
} from "@/lib/scoring";

export const WORLDS: Record<string, string[]> = {
  "Making things work": [
    "Architecture & Urban Planning", "Blue-Collar & Trade", "Electric Mobility & Battery Tech",
    "Engineering", "Engineering (Non-IT)", "Manufacturing & Industrial",
    "Manufacturing & Production", "Real Estate & Construction", "Semiconductors & Electronics",
    "Skilled Trades & Construction", "Transport & Logistics", "Transportation & Logistics",
  ],
  "Figuring things out": [
    "AI & Emerging Technologies", "Data & Analytics", "Frontier & Emerging",
    "Pharma & Biotech", "Science & Research", "Semiconductors & Electronics",
    "Space & Aerospace", "Technology & Software",
  ],
  "Looking after people": [
    "Education & Training", "Health & Social Care", "Healthcare & Medicine",
    "Hospitality & Tourism", "Personal & Community Services", "Social Work & NGO",
    "Sports & Fitness", "Sports & Recreation",
  ],
  "Making things people love": [
    "Arts, Culture & Heritage", "Creative Arts & Media", "Creator Economy & Digital",
    "Design & User Experience", "India-Specific Roles", "Journalism & Media",
    "Marketing & Communications", "Marketing & Growth",
  ],
  "Bringing people together": [
    "Sales & Business Development", "Human Resources", "Marketing & Growth",
    "Retail & Consumer Services", "Hospitality & Tourism",
  ],
  "Keeping things fair and running": [
    "FinTech & Digital Finance", "Finance & Accounting", "Government & Public Sector",
    "Legal & Compliance", "Military & Defense", "Public Service & Safety",
  ],
  "Looking after the planet": [
    "Agriculture & Environment", "Agriculture & Food Production", "Energy & Environment",
    "Green Tech & Climate Action", "Regional & Rural Specific",
  ],
  "Building something of your own": [
    "Business & Administration", "Consulting & Strategy", "Entrepreneurship & Startups",
    "Informal Sector & Gig Economy", "Product Management",
  ],
};

export type WorldName = keyof typeof WORLDS & string;

export const WORLD_NAMES = Object.keys(WORLDS) as WorldName[];

/** One grade-6 sentence per world. Read aloud test: a ten-year-old gets it. */
export const WORLD_BLURBS: Record<string, string> = {
  "Making things work":
    "People here build and fix the real things everyone uses every day.",
  "Figuring things out":
    "People here ask hard questions and keep going until they find the answer.",
  "Looking after people":
    "People here help others feel better, learn more, or get back on their feet.",
  "Making things people love":
    "People here make things that other people watch, read, wear or share.",
  "Bringing people together":
    "People here are good with people, and they help everyone pull the same way.",
  "Keeping things fair and running":
    "People here make sure things are fair, safe and running the way they should.",
  "Looking after the planet":
    "People here look after land, food, animals, water and the air we share.",
  "Building something of your own":
    "People here start things. They like being the one who decides what happens.",
};

const FAMILY_TO_WORLDS: Map<string, WorldName[]> = (() => {
  const m = new Map<string, WorldName[]>();
  for (const w of WORLD_NAMES) {
    for (const fam of WORLDS[w]) {
      const list = m.get(fam) ?? [];
      list.push(w);
      m.set(fam, list);
    }
  }
  return m;
})();

export function worldsForFamily(family: string): WorldName[] {
  return FAMILY_TO_WORLDS.get(family) ?? [];
}

export interface WorldCareerScore {
  ciId: string;
  title: string;
  family: string;
  score: number;
}

export interface WorldScore {
  world: WorldName;
  blurb: string;
  /** Mean match score of the world's top `sampleSize` careers. */
  score: number;
  /** How many careers of this world the learner is eligible for. */
  pool: number;
  /** Best-scoring careers in the world, family-diverse, for examples. */
  examples: WorldCareerScore[];
}

/**
 * The same six weighted components `matchCareers` uses, on one career.
 *
 * We can't call `matchCareers` here: it caps results at two per family,
 * which is exactly right for a top-10 of job titles and exactly wrong for
 * aggregating a world of twelve families. The arithmetic below is
 * identical to the body of `matchCareers` — the only thing dropped is the
 * presentation cap.
 */
function rawScore(L: Learner, c: Career): number {
  return (
    scoreArchetype(L, c) * WEIGHTS.archetype +
    scoreDomain(L, c) * WEIGHTS.domain +
    scoreContext(L, c).score * WEIGHTS.context +
    scoreValues(L, c).score * WEIGHTS.values +
    scoreSalary(L, c) * WEIGHTS.salary +
    scoreDemand(c) * WEIGHTS.demand
  );
}

export interface ScoreWorldsOptions {
  /** How many top careers make up a world's mean. Top-40 by default so a
   *  large world isn't diluted by its weakest roles. */
  sampleSize?: number;
  /** How many example titles to surface per world. */
  exampleCount?: number;
}

export function scoreWorlds(
  L: Learner,
  careers: Career[],
  opts: ScoreWorldsOptions = {},
): WorldScore[] {
  const sampleSize = opts.sampleSize ?? 40;
  const exampleCount = opts.exampleCount ?? 4;
  const cap = effectiveCapacity(L);

  const buckets = new Map<WorldName, WorldCareerScore[]>();
  for (const w of WORLD_NAMES) buckets.set(w, []);

  for (const c of careers) {
    const worlds = worldsForFamily(c.family);
    if (worlds.length === 0) continue;
    if (entryCost(c) > cap) continue;
    const score = rawScore(L, c);
    const entry: WorldCareerScore = {
      ciId: c.ciId, title: c.title, family: c.family,
      score: Math.round(score * 10) / 10,
    };
    for (const w of worlds) buckets.get(w)!.push(entry);
  }

  const out: WorldScore[] = WORLD_NAMES.map((world) => {
    // Codepoint tie-break — never localeCompare. ICU collation varies by
    // host and this ordering has to be identical in CI.
    const sorted = [...buckets.get(world)!].sort(
      (a, b) => b.score - a.score || (a.title < b.title ? -1 : a.title > b.title ? 1 : 0),
    );
    const top = sorted.slice(0, sampleSize);
    const mean = top.length
      ? top.reduce((s, r) => s + r.score, 0) / top.length
      : 0;

    // Family-diverse examples: no child should read a world as "it's all
    // one job". Fall back to plain top-N if the world has few families.
    const examples: WorldCareerScore[] = [];
    const seenFamilies = new Set<string>();
    for (const r of sorted) {
      if (seenFamilies.has(r.family)) continue;
      seenFamilies.add(r.family);
      examples.push(r);
      if (examples.length >= exampleCount) break;
    }
    for (const r of sorted) {
      if (examples.length >= exampleCount) break;
      if (!examples.includes(r)) examples.push(r);
    }

    return {
      world,
      blurb: WORLD_BLURBS[world],
      score: Math.round(mean * 10) / 10,
      pool: sorted.length,
      examples,
    };
  });

  return out.sort(
    (a, b) => b.score - a.score || (a.world < b.world ? -1 : a.world > b.world ? 1 : 0),
  );
}

/**
 * The four "kinds of work to peek at" an Explorer is shown.
 *
 * Extracted so the result page and the Career DNA™ snapshot can never
 * disagree: whatever the child sees on the result screen is exactly what the
 * dashboard, cards and report read afterwards. Family-diverse first pass,
 * then top-up, deterministic given the same worlds.
 */
export function pickPeekCareers(
  worlds: WorldScore[],
  count = 4,
): WorldCareerScore[] {
  const seenFamily = new Set<string>();
  const seenId = new Set<string>();
  const picked: WorldCareerScore[] = [];
  for (const pass of [true, false]) {
    for (const w of worlds) {
      for (const e of w.examples) {
        if (picked.length >= count) return picked;
        if (seenId.has(e.ciId)) continue;
        if (pass && seenFamily.has(e.family)) continue;
        seenId.add(e.ciId);
        seenFamily.add(e.family);
        picked.push(e);
      }
    }
  }
  return picked.slice(0, count);
}
